export type DateValue = string | number | Date | null | undefined;
export type UserClockFormat = '12-Hour' | '24-Hour';
export type UserDateFormat = 'MM/DD/YYYY' | 'YYYY/MM/DD' | 'DD/MM/YYYY';

type FormatDateWithPreferencesOptions = {
	timeZone?: string;
	dateFormat?: UserDateFormat;
	clockFormat?: UserClockFormat;
	locale?: string;
	includeSeconds?: boolean;
	onResolveBrowserDefaultTimeZone?: (timeZone: string) => void;
};

type FormatDateWithIntlOptions = {
	options: Intl.DateTimeFormatOptions;
	locale?: string;
	empty?: string;
	fallback?: string;
};

type RelativeTimeOptions = {
	empty?: string;
	fallback?: string;
};

type LongDateOptions = {
	locale?: string;
	empty?: string;
	fallback?: string;
};

const DEFAULT_DATE_FORMAT: UserDateFormat = 'DD/MM/YYYY';
const DEFAULT_CLOCK_FORMAT: UserClockFormat = '12-Hour';
const DEFAULT_LOCALE = 'en-US';

export const parseDateValue = (value: DateValue): Date | null => {
	if (value === null || value === undefined || value === '') {
		return null;
	}

	if (value instanceof Date) {
		return Number.isNaN(value.getTime()) ? null : value;
	}

	const parsed = new Date(String(value));
	return Number.isNaN(parsed.getTime()) ? null : parsed;
};

export const formatOrdinalDay = (day: number): string => {
	const mod10 = day % 10;
	const mod100 = day % 100;
	if (mod10 === 1 && mod100 !== 11) return `${day}st`;
	if (mod10 === 2 && mod100 !== 12) return `${day}nd`;
	if (mod10 === 3 && mod100 !== 13) return `${day}rd`;
	return `${day}th`;
};

export const formatDateWithIntl = (
	value: DateValue,
	{
		options,
		locale = DEFAULT_LOCALE,
		empty = '',
		fallback = '',
	}: FormatDateWithIntlOptions
): string => {
	const parsed = parseDateValue(value);
	if (!parsed) {
		return fallback || empty;
	}

	return new Intl.DateTimeFormat(locale, options).format(parsed);
};

export const formatLongDateLabel = (
	value: DateValue,
	{ locale = DEFAULT_LOCALE, empty = '', fallback = '' }: LongDateOptions = {}
): string => {
	const parsed = parseDateValue(value);
	if (!parsed) {
		return fallback || empty;
	}

	const weekday = new Intl.DateTimeFormat(locale, {
		weekday: 'long',
	}).format(parsed);
	const month = new Intl.DateTimeFormat(locale, { month: 'long' }).format(
		parsed
	);
	const time = new Intl.DateTimeFormat(locale, {
		hour: 'numeric',
		minute: '2-digit',
		hour12: true,
	}).format(parsed);

	return `${weekday} the ${formatOrdinalDay(parsed.getDate())} of ${month} at ${time}`;
};

export const formatRelativeTime = (
	value: DateValue,
	{ empty = '', fallback = '' }: RelativeTimeOptions = {}
): string => {
	const parsed = parseDateValue(value);
	if (!parsed) {
		return fallback || empty;
	}

	const diffInSeconds = Math.floor((Date.now() - parsed.getTime()) / 1000);
	if (diffInSeconds < 60) {
		return `${diffInSeconds} seconds ago`;
	}
	if (diffInSeconds < 3600) {
		const minutes = Math.floor(diffInSeconds / 60);
		return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`;
	}
	if (diffInSeconds < 86400) {
		const hours = Math.floor(diffInSeconds / 3600);
		return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
	}

	const days = Math.floor(diffInSeconds / 86400);
	return `${days} day${days !== 1 ? 's' : ''} ago`;
};

export const formatDateWithPreferences = (
	value: DateValue,
	{
		timeZone = 'UTC',
		dateFormat = DEFAULT_DATE_FORMAT,
		clockFormat = DEFAULT_CLOCK_FORMAT,
		locale = DEFAULT_LOCALE,
		includeSeconds = true,
		onResolveBrowserDefaultTimeZone,
	}: FormatDateWithPreferencesOptions = {}
): string => {
	if (value === null || value === undefined || value === '') {
		return '';
	}

	const parsed = parseDateValue(value);
	if (!parsed) {
		return String(value);
	}

	let resolvedTimeZone = timeZone;
	if (
		resolvedTimeZone === 'Browser Default' &&
		typeof Intl !== 'undefined' &&
		typeof Intl.DateTimeFormat === 'function'
	) {
		resolvedTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
		onResolveBrowserDefaultTimeZone?.(resolvedTimeZone);
	}

	const options: Intl.DateTimeFormatOptions = {
		timeZone: resolvedTimeZone || 'UTC',
		year: 'numeric',
		month: '2-digit',
		day: '2-digit',
		hour: '2-digit',
		minute: '2-digit',
		hour12: clockFormat === '12-Hour',
	};
	if (includeSeconds) {
		options.second = '2-digit';
	}

	const formatter = new Intl.DateTimeFormat(locale, options);
	const parts = formatter.formatToParts(parsed);
	const getPart = (type: Intl.DateTimeFormatPartTypes): string =>
		parts.find((part) => part.type === type)?.value || '';

	let formattedDate = '';
	switch (dateFormat) {
		case 'MM/DD/YYYY':
			formattedDate = `${getPart('month')}/${getPart('day')}/${getPart('year')}`;
			break;
		case 'YYYY/MM/DD':
			formattedDate = `${getPart('year')}/${getPart('month')}/${getPart('day')}`;
			break;
		case 'DD/MM/YYYY':
		default:
			formattedDate = `${getPart('day')}/${getPart('month')}/${getPart('year')}`;
			break;
	}

	let hours = getPart('hour');
	if (hours === '24') {
		hours = '00';
	}

	const period = options.hour12 ? ` ${getPart('dayPeriod')}` : '';
	const seconds = includeSeconds ? `:${getPart('second')}` : '';
	const time = `${hours}:${getPart('minute')}${seconds}${period}`;
	return `${formattedDate} ${time}`;
};

export function dateFormatter(): string {
	if (typeof localStorage === 'undefined') {
		return 'yyyy/MM/dd';
	}

	switch (localStorage.getItem('preferredDateFormat')) {
		case 'DD/MM/YYYY':
			return 'dd/MM/yyyy';
		case 'MM/DD/YYYY':
			return 'MM/dd/yyyy';
		case 'YYYY/MM/DD':
			return 'yyyy/MM/dd';
		default:
			return 'yyyy/MM/dd';
	}
}
